import { FocusEvent, HTMLAttributes, KeyboardEvent, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { Kbd } from "~/components/Kbd";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import {
  ENTITY_TYPE_PLURAL,
  entityHref,
} from "~/pages/manifold-nextgen/components/search-manager/constants";
import {
  SEARCH_ENTITY_TYPES,
  SearchEntityType,
  SearchHit,
  SEARCH_MAX_RESULTS,
  SearchResponse,
  searchPath,
  searchTotal,
} from "~/services/search";
import { HitPreview } from "./HitPreview";
import { HitTile } from "./HitTile";
import { QueryText } from "./QueryText";
import { ScopeFilter, typeList } from "./ScopeFilter";
import { HitDetails, useHitDetails } from "./useHitDetails";
import { useSlashToFocus } from "./useSlashToFocus";

/** Enough hits that every type with a match usually fills its group. */
const FETCH_LIMIT = 20;
const PER_GROUP = 3;
/** With one type there is only one group, so it gets the room every group would share. */
const PER_SCOPED_GROUP = 8;
const SEE_ALL = "see-all";

interface QuickSearchProps {
  /** Where "see all results" goes: the full search page for that query and scope. */
  seeAllHref: (query: string, scope: SearchEntityType | null) => string;
  inputClassName?: string;
}

/**
 * A search box for a page's hero. Focusing it opens a panel that picks which
 * types to search, and typing fills the panel with a few results of each type.
 * Enter hands the query and scope to the full search page, as does the last
 * option; arrow keys choose a result instead, with focus kept in the box.
 */
export const QuickSearch = ({ seeAllHref, inputClassName }: QuickSearchProps) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [scope, setScope] = useState<SearchEntityType | null>(null);
  const debouncedQuery = useDebounce(query).trim();
  const hasQuery = query.trim().length > 0 && debouncedQuery.length > 0;

  const { data, isLoading } = useFetcher<SearchResponse>(
    searchPath(debouncedQuery, scope ? [scope] : SEARCH_ENTITY_TYPES, FETCH_LIMIT, null),
    // Keep the last results open while the next keystroke's load.
    { keepPreviousData: true }
  );
  const hitDetails = useHitDetails();
  const navigate = useNavigate();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  useSlashToFocus(inputRef);

  const groups = hasQuery && data ? groupHits(data.hits, scope ? PER_SCOPED_GROUP : PER_GROUP) : [];
  const inaccessible = new Set(data?.inaccessible_types ?? []);
  const optionId = (key: string) => `${listboxId}-${key}`;
  const options = [
    ...groups.flatMap(({ hits }) =>
      hits.map((hit) => ({
        id: optionId(hitKey(hit)),
        href: entityHref(hit.entity_type, hit.entity_id),
      }))
    ),
    ...(groups.length > 0
      ? [{ id: optionId(SEE_ALL), href: seeAllHref(debouncedQuery, scope) }]
      : []),
  ];
  // None is active until the user moves to one, and typing or new results clear it.
  const activeIndex = options.findIndex((option) => option.id === activeId);
  const active = activeIndex === -1 ? undefined : options[activeIndex];
  const isExpanded = isOpen && hasQuery;
  const hitsMatchQuery = !isLoading && query.trim() === debouncedQuery;

  const open = (href: string) => {
    setIsOpen(false);
    navigate(href);
  };

  const moveActive = (step: number) => {
    const from = activeIndex === -1 ? (step > 0 ? -1 : 0) : activeIndex;
    const next = options[(from + step + options.length) % options.length];
    setActiveId(next.id);
    document.getElementById(next.id)?.scrollIntoView?.({ block: "nearest" });
  };

  // The scope pills are in the panel, so tabbing to them keeps it open.
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      // A chosen row opens once it is this query's; otherwise enter searches what was typed.
      if (active) {
        if (hitsMatchQuery) {
          event.preventDefault();
          open(active.href);
        }
      } else if (query.trim()) {
        event.preventDefault();
        open(seeAllHref(query.trim(), scope));
      }
      return;
    }
    if (!isExpanded || options.length === 0) {
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(event.key === "ArrowDown" ? 1 : -1);
    }
  };

  // Rows keep focus in the box, so a click doesn't close the results before it lands.
  const optionProps = (id: string, href: string): OptionProps => ({
    id,
    role: "option",
    "aria-selected": active?.id === id,
    onMouseDown: (event) => event.preventDefault(),
    onMouseMove: () => setActiveId(id),
    onClick: () => open(href),
  });

  return (
    <div
      className="relative"
      onBlur={handleBlur}
      onKeyDown={(event) => {
        // From a pill: close and go back to typing.
        if (event.key === "Escape" && event.target !== inputRef.current) {
          setIsOpen(false);
          inputRef.current?.focus();
        }
      }}
    >
      <Search
        className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        ref={inputRef}
        role="combobox"
        aria-label="Search"
        aria-keyshortcuts="/"
        aria-expanded={isExpanded}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={isExpanded && active ? active.id : undefined}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveId(null);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={`Search ${typeList(scope ? [scope] : SEARCH_ENTITY_TYPES)}`}
        autoComplete="off"
        spellCheck={false}
        className={cn(
          "h-12 rounded-xl pl-12 pr-12 text-base shadow-sm md:text-base",
          inputClassName
        )}
      />
      {!query && (
        <Kbd className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 border-border bg-muted text-muted-foreground">
          /
        </Kbd>
      )}

      {isOpen && (
        <div className="absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95 slide-in-from-top-2">
          {/* A click on a pill keeps focus in the box, so typing carries on. */}
          <div
            className={cn("px-3 py-2.5", hasQuery && "border-b border-border")}
            onMouseDown={(event) => event.preventDefault()}
          >
            <ScopeFilter value={scope} onChange={setScope} inaccessible={inaccessible} size="sm" />
          </div>
          {hasQuery && !data && isLoading && <PanelSkeleton />}
          {hasQuery && data && !isLoading && groups.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              No results for <QueryText>{debouncedQuery}</QueryText>
              {scope && ` in ${ENTITY_TYPE_PLURAL[scope].toLowerCase()}`}
            </p>
          )}
          {groups.length > 0 && (
            <div
              id={listboxId}
              role="listbox"
              aria-label="Results"
              className="flex max-h-112 flex-col"
            >
              {/* Only the groups scroll, so "see all" stays in view. */}
              <div className="min-h-0 flex-1 overflow-y-auto py-1.5">
                {groups.map(({ entityType, hits }) => (
                  <div key={entityType} role="group" aria-label={ENTITY_TYPE_PLURAL[entityType]}>
                    <div
                      className="px-4 pb-1 pt-2.5 text-xs font-medium text-muted-foreground"
                      aria-hidden="true"
                    >
                      {ENTITY_TYPE_PLURAL[entityType]}
                    </div>
                    {hits.map((hit) => {
                      const id = optionId(hitKey(hit));
                      return (
                        <QuickResult
                          key={id}
                          hit={hit}
                          details={hitDetails(hit)}
                          query={debouncedQuery}
                          option={optionProps(id, entityHref(hit.entity_type, hit.entity_id))}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="border-t border-border py-1.5">
                <div
                  {...optionProps(optionId(SEE_ALL), seeAllHref(debouncedQuery, scope))}
                  className={OPTION_CLASS}
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <Search className="size-3.5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                    {seeAllLabel(data && hitsMatchQuery ? searchTotal(data) : null)} for{" "}
                    <QueryText>{debouncedQuery}</QueryText>
                  </span>
                  <ArrowRight
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OPTION_CLASS =
  "mx-1.5 flex h-10 cursor-pointer items-center gap-3 rounded-lg px-2.5 aria-selected:bg-accent";

type OptionProps = HTMLAttributes<HTMLDivElement> & { id: string };

interface QuickResultProps {
  hit: SearchHit;
  details: HitDetails | undefined;
  query: string;
  option: OptionProps;
}

/** One line: the title, then the text it matched on. */
const QuickResult = ({ hit, details, query, option }: QuickResultProps) => (
  <div {...option} className={OPTION_CLASS}>
    <HitTile hit={hit} details={details} size="sm" />
    <span className="min-w-0 max-w-3/5 shrink-0 truncate text-sm text-foreground">
      {hit.title || hit.entity_id}
    </span>
    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
      <HitPreview hit={hit} details={details} query={query} />
    </span>
  </div>
);

// Mirrors a result line: tile beside a title bar.
const PanelSkeleton = () => (
  <div role="status" aria-label="Searching" className="py-1.5">
    {Array.from({ length: PER_GROUP }, (_, index) => (
      <div key={index} aria-hidden="true" className="mx-1.5 flex h-10 items-center gap-3 px-2.5">
        <Skeleton className="size-6 rounded-md" />
        <Skeleton className="h-3.5 w-2/5" />
      </div>
    ))}
  </div>
);

interface HitGroup {
  entityType: SearchEntityType;
  hits: SearchHit[];
}

/** Up to `perGroup` hits per type, the types in the order their best hit ranked. */
function groupHits(hits: SearchHit[], perGroup: number): HitGroup[] {
  const groups = new Map<SearchEntityType, SearchHit[]>();
  hits.forEach((hit) => {
    const group = groups.get(hit.entity_type) ?? [];
    if (group.length < perGroup) {
      group.push(hit);
    }
    groups.set(hit.entity_type, group);
  });
  return Array.from(groups, ([entityType, groupHits]) => ({ entityType, hits: groupHits }));
}

/** Names the count when the results on screen are this query's, and the cap past it. */
function seeAllLabel(total: number | null): string {
  if (total === null || total === 0) {
    return "See all results";
  }
  if (total === SEARCH_MAX_RESULTS) {
    return `See the top ${SEARCH_MAX_RESULTS} results`;
  }
  return `See all ${total} ${total === 1 ? "result" : "results"}`;
}

function hitKey(hit: SearchHit): string {
  return `${hit.entity_type}-${hit.entity_id}`;
}
